import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Lock, Send } from "lucide-react";

import { api, isMembershipError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { REQUEST_NOUN } from "@/lib/constants";
import { useToast } from "@/components/toast";
import { Modal } from "@/components/modal";
import { Button } from "@/components/ui";

export function ConfirmRequestDialog({ target, onClose, onSent }) {
  const { user } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [loading, setLoading] = useState(false);
  const noun = user?.request_type ? REQUEST_NOUN[user.request_type] : "Request";

  const send = async () => {
    if (!target) return;
    setLoading(true);
    try {
      await api("/requests", { method: "POST", body: { recipient_id: target.id } });
      toast(`${noun} sent to ${target.display_name}`, "success");
      ["matches", "daily", "requests"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      qc.invalidateQueries({ queryKey: ["member", target.id] });
      onSent?.();
      onClose();
    } catch (e) {
      if (isMembershipError(e)) onClose(); // the paywall takes over
      else toast(e.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={!!target} onClose={onClose} title={user?.request_label ?? "Send request"} testId="confirm-request-sheet">
      <div className="mb-6 flex gap-3 rounded-xl bg-[#F6E3E4] p-4">
        <Lock className="h-5 w-5 shrink-0 text-primary" />
        <p className="text-sm leading-5" data-testid="confirm-request-text">
          You are about to send a {noun} to this member. They must accept before your WhatsApp contact details can be shared.
        </p>
      </div>
      <div className="flex gap-3">
        <Button testId="confirm-request-cancel" title="Cancel" variant="outline" className="flex-1" onClick={onClose} />
        <Button testId="confirm-request-send" title="Send" icon={Send} loading={loading} className="flex-1" onClick={send} />
      </div>
    </Modal>
  );
}
